import {
  AttendeeInfoFormType,
  AttendeeRequiredFieldsType,
  FanGroupOwnerEventEntity,
  Option,
  attendeeFields,
} from '@seaters/constants';
import {
  AccessibleDatePicker,
  AccessibleSelect,
  PhoneInputWithCountryCode,
} from '@seaters/ui';
import { Form, Input, Typography } from 'antd';
import dayjs from 'dayjs';

const { Text } = Typography;
import { useEffect, useState, type JSX } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useFanGroupPrimaryColor } from '../../hooks';

// Declared in @seaters/constants so the conditional attendee schema can be
// typed against it; re-exported here for the checkout components.
export type { AttendeeInfoFormType };

export const attendeeInfoLabel: {
  [key in AttendeeRequiredFieldsType]: string;
} = {
  company: 'checkout_att_field_company',
  title: 'checkout_att_field_title',
  city: 'checkout_att_field_city',
  email: 'checkout_att_field_email',
  firstName: 'checkout_att_field_firstname',
  lastName: 'checkout_att_field_lastname',
  language: 'checkout_att_field_language',
  country: 'checkout_att_field_country',
  idNumber: 'checkout_att_field_passportid',
  birthDate: 'checkout_att_field_dateofbirth',
  citizenshipCountryCode: 'checkout_att_field_citizenship',
  phoneNumber: 'checkout_att_field_phone',
  zipCode: 'checkout_att_field_zip',
  address: 'checkout_att_field_address',
  state: 'checkout_att_field_state',
};

export type AttendeeInfoFormProps = {
  fields: FanGroupOwnerEventEntity['attendeeRequiredFields'] | undefined;
  languagesOptions?: Option[];
  countriesOptions?: Option[];
  onFormSubmit: (formData: AttendeeInfoFormType) => void;
  defaultValues: AttendeeInfoFormType;
  /** True when the attendee is the person filling in the form, so their own fields get autocomplete tokens (WCAG 1.3.5). */
  isPrimaryAttendee?: boolean;
};

const titleOptions = [
  {
    label: 'settings_personal-information_title-mr',
    value: 'MR',
  },
  {
    label: 'settings_personal-information_title-ms',
    value: 'MS',
  },
];

export const LanguageField = ({
  formItemStyle,
  errors,
  control,
  languagesOptions,
  inputSize = 'middle',
}: {
  formItemStyle: {
    width: string;
  };
  errors: FieldErrors<AttendeeInfoFormType>;
  control: Control<AttendeeInfoFormType, any>;
  languagesOptions?: Option[];
  inputSize?: 'large' | 'middle' | 'small';
}) => {
  const { t } = useTranslation();
  return (
    <Form.Item
      style={formItemStyle}
      name="language"
      label={<Text>{t('settings_personal-section_form_lang-label')}</Text>}
      htmlFor="attendee-language"
      validateStatus={errors && errors.language && errors.language && 'error'}
      help={
        errors?.language?.message ? (
          <span aria-hidden="true" id="attendee-language-error">
            {errors?.language?.message}
          </span>
        ) : null
      }
    >
      <Controller
        control={control}
        rules={{
          required: t('general_label_required')
            ? (t('general_label_required') as string)
            : 'Required',
        }}
        name={`language`}
        render={({ field }) => (
          <AccessibleSelect
            id="attendee-language"
            options={languagesOptions}
            {...field}
            size={inputSize}
            aria-invalid={errors && errors?.language ? 'true' : 'false'}
            aria-describedby={
              errors && errors?.language ? 'attendee-language-error' : undefined
            }
          />
        )}
      />
    </Form.Item>
  );
};

export const AddressFields = ({
  formItemStyle,
  control,
  errors,
  inputSize = 'middle',
}: {
  formItemStyle: {
    width: string;
  };
  control: Control<AttendeeInfoFormType, any>;
  errors: FieldErrors<AttendeeInfoFormType>;
  inputSize?: 'large' | 'middle' | 'small';
}) => {
  const { t } = useTranslation();
  return (
    <>
      <Form.Item
        style={formItemStyle}
        name={`address.line1`}
        label={
          <Text>{t('settings_personal-information_address-label-line1')}</Text>
        }
        required
        htmlFor="attendee-address-line1"
        validateStatus={
          errors && errors.address && errors.address?.line1 && 'error'
        }
        help={
          errors.address?.line1?.message ? (
            <span aria-hidden="true" id="attendee-address-line1-error">
              {errors.address?.line1?.message}
            </span>
          ) : null
        }
      >
        <Controller
          control={control}
          rules={{
            required: t('general_label_required')
              ? (t('general_label_required') as string)
              : 'Required',
          }}
          name={`address.line1`}
          render={({ field }) => (
            <Input
              id="attendee-address-line1"
              {...field}
              size={inputSize}
              aria-invalid={
                errors && errors.address && errors.address?.line1
                  ? 'true'
                  : 'false'
              }
              aria-describedby={
                errors && errors.address && errors.address?.line1
                  ? 'attendee-address-line1-error'
                  : undefined
              }
            />
          )}
        />
      </Form.Item>
      <Form.Item
        style={formItemStyle}
        name={`address.line2`}
        label={
          <Text>{t('settings_personal-information_address-label-line2')}</Text>
        }
        htmlFor="attendee-address-line2"
      >
        <Controller
          control={control}
          name={`address.line2`}
          render={({ field }) => (
            <Input id="attendee-address-line2" {...field} size={inputSize} />
          )}
        />
      </Form.Item>
      <Form.Item
        style={formItemStyle}
        name={`address.line3`}
        label={
          <Text>{t('settings_personal-information_address-label-line3')}</Text>
        }
        htmlFor="attendee-address-line3"
      >
        <Controller
          control={control}
          name={`address.line3`}
          render={({ field }) => (
            <Input id="attendee-address-line3" {...field} size={inputSize} />
          )}
        />
      </Form.Item>
    </>
  );
};

export const ZipCodeField = ({
  formItemStyle,
  errors,
  control,
  inputSize = 'middle',
}: {
  formItemStyle: {
    width: string;
  };
  errors: FieldErrors<AttendeeInfoFormType>;
  control: Control<AttendeeInfoFormType, any>;
  inputSize?: 'large' | 'middle' | 'small';
}) => {
  const { t } = useTranslation();
  return (
    <Form.Item
      style={formItemStyle}
      name={`address.zipCode`}
      label={<Text>{t(attendeeInfoLabel.zipCode)}</Text>}
      required
      htmlFor="attendee-address-zipCode"
      validateStatus={
        errors && errors.address && errors.address.zipCode && 'error'
      }
      help={
        errors?.address?.zipCode?.message ? (
          <span aria-hidden="true" id="attendee-address-zipCode-error">
            {errors?.address?.zipCode?.message}
          </span>
        ) : null
      }
    >
      <Controller
        control={control}
        rules={{
          required: t('general_label_required')
            ? (t('general_label_required') as string)
            : 'Required',
        }}
        name={`address.zipCode`}
        render={({ field }) => (
          <Input
            id="attendee-address-zipCode"
            {...field}
            size={inputSize}
            aria-invalid={
              errors && errors.address && errors.address.zipCode
                ? 'true'
                : 'false'
            }
            aria-describedby={
              errors && errors.address && errors.address.zipCode
                ? 'attendee-address-zipCode-error'
                : undefined
            }
          />
        )}
      />
    </Form.Item>
  );
};

export const TitleField = ({
  formItemStyle,
  errors,
  control,
  inputSize = 'middle',
}: {
  formItemStyle: {
    width: string;
  };
  errors: FieldErrors<AttendeeInfoFormType>;
  control: Control<AttendeeInfoFormType, any>;
  inputSize?: 'large' | 'middle' | 'small';
}) => {
  const { t } = useTranslation();
  return (
    <Form.Item
      style={formItemStyle}
      name={`title`}
      label={<Text>{t(attendeeInfoLabel.title)}</Text>}
      required
      htmlFor="attendee-title"
      validateStatus={errors && errors.title && 'error'}
      help={
        errors?.title?.message ? (
          <span aria-hidden="true" id="attendee-title-error">
            {errors?.title?.message}
          </span>
        ) : null
      }
    >
      <Controller
        control={control}
        rules={{
          required: t('general_label_required')
            ? (t('general_label_required') as string)
            : 'Required',
        }}
        name={`title`}
        render={({ field }) => (
          <AccessibleSelect
            id="attendee-title"
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            size={inputSize}
            aria-invalid={errors && errors.title ? 'true' : 'false'}
            aria-describedby={
              errors && errors.title ? 'attendee-title-error' : undefined
            }
            options={titleOptions.map((el) => ({
              ...el,
              label: t(el.label),
            }))}
          />
        )}
      />
    </Form.Item>
  );
};

export const CityField = ({
  formItemStyle,
  errors,
  control,
  inputSize = 'middle',
}: {
  formItemStyle: {
    width: string;
  };
  errors: FieldErrors<AttendeeInfoFormType>;
  control: Control<AttendeeInfoFormType, any>;
  inputSize?: 'large' | 'middle' | 'small';
}) => {
  const { t } = useTranslation();
  return (
    <Form.Item
      style={formItemStyle}
      name={`address.city`}
      label={<Text>{t(attendeeInfoLabel.city)}</Text>}
      required
      htmlFor="attendee-address-city"
      validateStatus={
        errors && errors.address && errors.address.city && 'error'
      }
      help={
        errors?.address?.city?.message ? (
          <span aria-hidden="true" id="attendee-address-city-error">
            {errors?.address?.city?.message}
          </span>
        ) : null
      }
    >
      <Controller
        control={control}
        rules={{
          required: t('general_label_required')
            ? (t('general_label_required') as string)
            : 'Required',
        }}
        name={`address.city`}
        render={({ field }) => (
          <Input
            id="attendee-address-city"
            {...field}
            size={inputSize}
            aria-invalid={
              errors && errors.address && errors.address.city ? 'true' : 'false'
            }
            aria-describedby={
              errors && errors.address && errors.address.city
                ? 'attendee-address-city-error'
                : undefined
            }
          />
        )}
      />
    </Form.Item>
  );
};

export const CountryField = ({
  formItemStyle,
  errors,
  control,
  countriesOptions,
  inputSize = 'middle',
}: {
  formItemStyle: {
    width: string;
  };
  errors: FieldErrors<AttendeeInfoFormType>;
  control: Control<AttendeeInfoFormType, any>;
  countriesOptions?: Option[];
  inputSize?: 'large' | 'middle' | 'small';
}) => {
  const { t } = useTranslation();
  return (
    <Form.Item
      style={formItemStyle}
      name={`country`}
      label={<Text>{t(attendeeInfoLabel.country)}</Text>}
      required
      htmlFor="attendee-country"
      validateStatus={errors && errors.country && 'error'}
      help={
        errors?.country?.message ? (
          <span aria-hidden="true" id="attendee-country-error">
            {errors?.country?.message}
          </span>
        ) : null
      }
    >
      <Controller
        control={control}
        name={'country'}
        render={({ field }) => (
          <AccessibleSelect
            id="attendee-country"
            {...field}
            size={inputSize}
            aria-invalid={errors && errors.country ? 'true' : 'false'}
            aria-describedby={
              errors && errors.country ? 'attendee-country-error' : undefined
            }
            showSearch
            options={countriesOptions}
          />
        )}
      />
    </Form.Item>
  );
};

export const CitizenshipField = ({
  formItemStyle,
  errors,
  control,
  countriesOptions,
  inputSize = 'middle',
}: {
  formItemStyle: {
    width: string;
  };
  errors: FieldErrors<AttendeeInfoFormType>;
  control: Control<AttendeeInfoFormType, any>;
  countriesOptions?: Option[];
  inputSize?: 'large' | 'middle' | 'small';
}) => {
  const { t } = useTranslation();
  return (
    <Form.Item
      style={formItemStyle}
      name={`citizenshipCountryCode`}
      label={<Text>{t(attendeeInfoLabel.citizenshipCountryCode)}</Text>}
      validateStatus={errors && errors.citizenshipCountryCode && 'error'}
      help={
        errors?.citizenshipCountryCode?.message ? (
          <span aria-hidden="true" id="attendee-citizenship-country-error">
            {errors?.citizenshipCountryCode?.message}
          </span>
        ) : null
      }
      htmlFor="attendee-citizenship-country"
      aria-autocomplete="none"
      required={
        attendeeFields.find((field) => field.name === 'citizenshipCountryCode')
          ?.required
      }
    >
      <Controller
        control={control}
        name={`citizenshipCountryCode`}
        render={({ field }) => (
          <AccessibleSelect
            id="attendee-citizenship-country"
            {...field}
            size={inputSize}
            showSearch
            options={countriesOptions}
            aria-invalid={
              errors && errors.citizenshipCountryCode ? 'true' : 'false'
            }
            aria-describedby={
              errors && errors.citizenshipCountryCode
                ? 'attendee-citizenship-country-error'
                : undefined
            }
          />
        )}
      />
    </Form.Item>
  );
};

export const StateField = ({
  formItemStyle,
  errors,
  control,
  inputSize = 'middle',
}: {
  formItemStyle: {
    width: string;
  };
  errors: FieldErrors<AttendeeInfoFormType>;
  control: Control<AttendeeInfoFormType, any>;
  inputSize?: 'large' | 'middle' | 'small';
}) => {
  const { t } = useTranslation();
  return (
    <Form.Item
      style={formItemStyle}
      name={`address.state`}
      label={<Text>{t(attendeeInfoLabel.state)}</Text>}
      required
      htmlFor="attendee-address-state"
      validateStatus={
        errors && errors.address && errors.address.state && 'error'
      }
      help={
        errors?.address?.state?.message ? (
          <span aria-hidden="true" id="attendee-address-state-error">
            {errors?.address?.state?.message}
          </span>
        ) : null
      }
    >
      <Controller
        control={control}
        name={`address.state`}
        render={({ field }) => (
          <Input
            id="attendee-address-state"
            {...field}
            size={inputSize}
            aria-invalid={
              errors && errors.address && errors.address.state
                ? 'true'
                : 'false'
            }
            aria-describedby={
              errors && errors.address && errors.address.state
                ? 'attendee-address-state-error'
                : undefined
            }
          />
        )}
      />
    </Form.Item>
  );
};

export const BirthDateField = ({
  formItemStyle,
  errors,
  control,
  inputSize = 'middle',
}: {
  formItemStyle: {
    width: string;
  };
  errors: FieldErrors<AttendeeInfoFormType>;
  control: Control<AttendeeInfoFormType, any>;
  inputSize?: 'large' | 'middle' | 'small';
}) => {
  const { t } = useTranslation();

  const { primaryColor: primaryCustomColor } = useFanGroupPrimaryColor();

  return (
    <Form.Item
      style={formItemStyle}
      name={`birthDate`}
      label={<Text>{t(attendeeInfoLabel.birthDate)}</Text>}
      htmlFor="attendee-birthDate"
      required={
        attendeeFields.find((field) => field.name === 'birthDate')?.required
      }
      validateStatus={errors && errors.birthDate && 'error'}
      help={
        errors && errors.birthDate && errors.birthDate.message ? (
          <span aria-hidden="true" id="attendee-birthDate-error">
            {errors && errors.birthDate && errors.birthDate.message}
          </span>
        ) : null
      }
    >
      <Controller
        control={control}
        name={`birthDate`}
        render={({ field }) => {
          return (
            <AccessibleDatePicker
              format="MM-DD-YYYY"
              {...field}
              value={field.value?.toDate() ?? undefined}
              onChange={(date) => field.onChange(dayjs(date))}
              required
              showMonthYearSelect
              primaryColor={primaryCustomColor}
              error={!!(errors && errors.birthDate)}
              aria-describedby={
                errors && errors.birthDate
                  ? 'attendee-birthDate-error'
                  : undefined
              }
              inputProps={{
                size: inputSize,
                id: 'attendee-birthDate',
              }}
            />
          );
        }}
      />
    </Form.Item>
  );
};

export const MobileField = ({
  formItemStyle,
  control,
  defaultValues,
  setValue,
  errors,
  inputSize = 'middle',
}: {
  formItemStyle: {
    width: string;
  };
  errors: FieldErrors<AttendeeInfoFormType>;
  control: Control<AttendeeInfoFormType, any>;
  defaultValues: any;
  setValue: UseFormSetValue<any>;
  inputSize?: 'large' | 'middle' | 'small';
}): JSX.Element => {
  const { t } = useTranslation();
  const [phoneNumber, setPhoneNumber] = useState(
    defaultValues?.phoneNumber?.countryCallingCode &&
      defaultValues?.phoneNumber?.localNumber
      ? defaultValues?.phoneNumber?.countryCallingCode +
          defaultValues?.phoneNumber?.localNumber
      : ''
  );

  const [countryCode, setCountryCode] = useState(
    defaultValues?.phoneNumber?.countryCallingCode ?? ''
  );

  useEffect(() => {
    if (phoneNumber) {
      setValue(
        'phoneNumber.localNumber',
        phoneNumber?.slice(countryCode?.length)
      );
    }
    if (countryCode) {
      setValue('phoneNumber.countryCallingCode', countryCode);
    }
  }, [phoneNumber, countryCode]);

  return (
    <Form.Item
      style={formItemStyle}
      label={<Text>{t('phoneNumber')}</Text>}
      name={`phoneNumber.localNumber`}
      htmlFor="attendee-phoneNumber"
      validateStatus={errors && errors.phoneNumber && 'error'}
      help={
        errors &&
        errors.phoneNumber &&
        errors.phoneNumber.localNumber?.message ? (
          <span aria-hidden="true" id="attendee-phoneNumber-error">
            {errors &&
              errors.phoneNumber &&
              errors.phoneNumber.localNumber?.message}
          </span>
        ) : null
      }
      required={
        attendeeFields.find((field) => field.name === 'phoneNumber')?.required
      }
    >
      <Controller
        control={control}
        name={`phoneNumber.localNumber`}
        render={({ field }) => {
          return (
            <PhoneInputWithCountryCode
              {...field}
              inputId="attendee-phoneNumber"
              isError={!!errors.phoneNumber}
              phoneNumber={phoneNumber}
              setPhoneNumber={setPhoneNumber}
              countryCode={countryCode}
              setCountryCode={setCountryCode}
              size={inputSize}
              aria-invalid={errors && errors.phoneNumber ? 'true' : 'false'}
              aria-describedby={
                errors && errors.phoneNumber
                  ? 'attendee-phoneNumber-error'
                  : undefined
              }
            />
          );
        }}
      />
    </Form.Item>
  );
};
